import React, { ChangeEvent, createRef, FocusEvent, useState, useEffect } from "react";
import { Text, Box, Flex, Input } from "@chakra-ui/react";
import { ClasswizeSelect, ClasswizeSelectOption } from "./ui/Select";
import { convert24HrTo12Hr, determineMeridiemFrom24Hr, TimeMeridiem } from "@/utils/dateTimeUtils";

export type TimeInput24Hr = {
	hour: number;
	minute: number;
};

const meridiemOptions: ClasswizeSelectOption<TimeMeridiem>[] = Object.values(TimeMeridiem).map((meridiem) => ({
	label: meridiem,
	value: meridiem,
}));

interface Props {
	value?: TimeInput24Hr;
	handleChange: (value: TimeInput24Hr) => void;
}

/**
 * Allows the user to input a time in the format of hh:mm AM/PM.
 *
 * It will automatically convert the hour to 12hr format and switch the meridiem when the user inputs a 24hr hour.
 *
 * @example
 * ```tsx
 * // Create state for the time
 * const [time, setTime] = useState<TimeInput24Hr>({ hour: 23, minute: 0 });
 *
 * // Use the TimeInput component to allow the user to input the time
 * <TimeInput value={time} handleChange={setTime} />
 * ```
 */
export function TimeInput(props: Props): JSX.Element {
	const inputHourRef = createRef<HTMLInputElement>();
	const inputMinuteRef = createRef<HTMLInputElement>();
	const inputMeridiemRef = createRef<HTMLButtonElement>();

	const initialHour12 = props.value ? convert24HrTo12Hr(props.value.hour) : 0;

	const [inputHour, setInputHour] = useState<string>(props.value ? initialHour12.toString().padStart(2, "0") : "");
	const [inputMinute, setInputMinute] = useState<string>(props.value?.minute.toString().padStart(2, "0") ?? "");
	const [inputMeridiem, setInputMeridiem] = useState<TimeMeridiem>(determineMeridiemFrom24Hr(props.value?.hour ?? 0));

	// Update display values when props.value changes
	useEffect(() => {
		if (props.value) {
			const hour12 = convert24HrTo12Hr(props.value.hour);
			setInputHour(hour12.toString().padStart(2, "0"));
			setInputMinute(props.value.minute.toString().padStart(2, "0"));
			setInputMeridiem(determineMeridiemFrom24Hr(props.value.hour));
		} else {
			setInputHour("");
			setInputMinute("");
			setInputMeridiem(TimeMeridiem.AM);
		}
	}, [props.value]);

	/**
	 * Converts 12-hour format to 24-hour format and calls props.handleChange
	 */
	function notifyParentOfChange(hour12: number, minute: number, meridiem: TimeMeridiem) {
		let hour24: number;

		if (meridiem === TimeMeridiem.AM) {
			hour24 = hour12 === 12 ? 0 : hour12;
		} else {
			hour24 = hour12 === 12 ? 12 : hour12 + 12;
		}

		props.handleChange({ hour: hour24, minute });
	}

	function handleInputSelect(e: FocusEvent<HTMLInputElement>) {
		// Select all text when the input is clicked or tabbed to
		e.target.select();
	}

	function handleHourChange(e: ChangeEvent<HTMLInputElement>) {
		// enforce number only input
		if (!/^\d*$/.test(e.target.value)) {
			return;
		}

		// Limit the input to 2 digits
		if (e.target.value.length <= 2) {
			setInputHour(e.target.value);
		}
		// Move to the minute box after entering a valid hour
		if (e.target.value.length === 2 && Number(e.target.value) >= 0 && Number(e.target.value) <= 23) {
			inputMinuteRef.current?.focus();
		}
	}

	/**
	 * Handles when focus is lost from the hour input (e.g. when the user clicks outside the input, tabs to the next input, etc.)
	 * Converts the hour to 12hr format if given in 24hr format, and prepends a 0 if the hour is a single digit.
	 */
	function handleHourBlur(e: FocusEvent<HTMLInputElement>) {
		// If the input is empty, don't auto-format it - keep it empty
		if (e.target.value.trim() === "") {
			return;
		}

		let hour = Number(e.target.value);
		let meridiem: TimeMeridiem;

		if (hour < 0) {
			hour = 0;
		} else if (hour > 23) {
			hour = 23;
		}

		if (hour === 0) {
			meridiem = TimeMeridiem.AM;
			hour = 12;
		} else if (hour === 12) {
			meridiem = TimeMeridiem.PM;
			// hour stays 12
		} else if (hour > 12 && hour <= 23) {
			meridiem = TimeMeridiem.PM;
			hour -= 12;
		} else {
			meridiem = inputMeridiem;
		}

		const formattedHour = hour.toString().padStart(2, "0");
		setInputHour(formattedHour);
		setInputMeridiem(meridiem);

		// Only notify parent if both fields have content
		if (inputMinute.trim() !== "") {
			const currentMinute = Number(inputMinute);
			notifyParentOfChange(hour, currentMinute, meridiem);
		}
	}

	function handleMinuteChange(e: ChangeEvent<HTMLInputElement>) {
		// enforce number only input
		if (!/^\d*$/.test(e.target.value)) {
			return;
		}

		// Limit the input to 2 digits
		if (e.target.value.length <= 2) {
			setInputMinute(e.target.value);
		}

		// Move to the meridiem box after entering a valid minute
		if (e.target.value.length === 2 && Number(e.target.value) >= 0 && Number(e.target.value) <= 59) {
			inputMeridiemRef.current?.focus();
		}
	}

	function handleMinuteBlur(e: FocusEvent<HTMLInputElement>) {
		// If the input is empty, don't auto-format it - keep it empty
		if (e.target.value.trim() === "") {
			return;
		}

		let minute = Number(e.target.value);

		if (minute < 0) {
			minute = 0;
		} else if (minute > 59) {
			minute = 59;
		}

		const formattedMinute = minute.toString().padStart(2, "0");
		setInputMinute(formattedMinute);

		// Only notify parent if both fields have content
		if (inputHour.trim() !== "") {
			const currentHour = Number(inputHour);
			notifyParentOfChange(currentHour, minute, inputMeridiem);
		}
	}

	function handleMeridiemChange(value: unknown) {
		const meridiem = value as TimeMeridiem;
		setInputMeridiem(meridiem);

		// Only notify parent if both fields have content
		if (inputHour.trim() !== "" && inputMinute.trim() !== "") {
			const currentHour = Number(inputHour);
			const currentMinute = Number(inputMinute);
			notifyParentOfChange(currentHour, currentMinute, meridiem);
		}
	}

	return (
		<Box>
			<Flex alignItems="center" gap={1}>
				<Input
					width="45px"
					bg="white"
					border="1px solid"
					borderColor="neutrals.50"
					data-testid="hour-input"
					ref={inputHourRef}
					value={inputHour}
					placeholder="hh"
					type="text"
					inputMode="numeric"
					min={0}
					max={23}
					onBlur={handleHourBlur}
					onChange={handleHourChange}
					onFocus={handleInputSelect}
					px={1}
					textAlign={"center"}
				/>
				<Text fontSize="lg">:</Text>
				<Input
					width="45px"
					bg="white"
					border="1px solid"
					borderColor="neutrals.50"
					data-testid="minute-input"
					ref={inputMinuteRef}
					value={inputMinute}
					placeholder="mm"
					type="text"
					inputMode="numeric"
					min={0}
					max={59}
					onBlur={handleMinuteBlur}
					onChange={handleMinuteChange}
					onFocus={handleInputSelect}
					px={1}
					textAlign={"center"}
				/>
				<ClasswizeSelect
					value={inputMeridiem}
					onChange={handleMeridiemChange}
					options={meridiemOptions}
					width={"70px"}
					ref={inputMeridiemRef}
				/>
			</Flex>
		</Box>
	);
}
